/**
 * CurriculumMap — collapsible panel at the top of the LMS Admin Courses
 * tab. Shows what's already taught for a chosen tier + surfaces weak
 * areas so admins know what to build next.
 *
 * Sources of truth:
 *   GET /api/admin/lms/curriculum-map?tier=<tier>
 *
 * The panel is a pure viewer + gap surfacer — it never mutates courses
 * itself. It exposes two callbacks so LMSAdmin owns the mutations:
 *   onBuildForTier(tier, seedTopic?) — opens CourseBlueprintModal seeded
 *   onSuggestForTier(tier)           — triggers the AI suggestions flow
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  ChevronDown, ChevronRight, Sparkles, Map as MapIcon, Layers, BookOpen,
  AlertTriangle, CheckCircle2, Plus, Network, Route, Loader2, Save, Lock,
} from 'lucide-react';

const TIERS = [
  { value: 'free',       label: 'Free' },
  { value: 'pro',        label: 'Pro' },
  { value: 'business',   label: 'Business' },
  { value: 'enterprise', label: 'Enterprise' },
];

const COVERAGE_META = {
  thin:   { color: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200', label: 'Thin' },
  medium: { color: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200', label: 'Medium' },
  strong: { color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200', label: 'Strong' },
};

function StatTile({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-center min-w-[80px]">
      <div className="text-lg font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function CurriculumMap({ onBuildForTier, onSuggestForTier, onOpenCourse }) {
  const { API } = useContext(AuthContext);
  // Collapsed by default so the semantic-clustering call doesn't fire
  // on page load. Admin can expand + analyze on demand.
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState('free');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState({});
  const [path, setPath] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [expandedThemes, setExpandedThemes] = useState({});
  const [savedPath, setSavedPath] = useState(null);
  const [savingPath, setSavingPath] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Fresh tier → drop any stale learning path so the user has to
      // re-request it (LLM call is ~5s; don't fire it eagerly).
      setPath(null);
      try {
        const { data: d } = await axios.get(`${API}/admin/lms/curriculum-map?tier=${tier}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) toast.error('Could not load curriculum map');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, tier, open]);

  const loadLearningPath = async () => {
    setPathLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/lms/learning-path?tier=${tier}`);
      setPath(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute learning path');
    } finally {
      setPathLoading(false);
    }
  };

  // Fetch any previously-saved path + enforcement flag when the tier
  // changes, so admins see whether members are already governed by it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/lms/learning-path/${tier}`);
        if (!cancelled) setSavedPath(d);
      } catch {
        if (!cancelled) setSavedPath(null);
      }
    })();
    return () => { cancelled = true; };
  }, [API, tier]);

  const savePath = async () => {
    if (!path?.steps?.length) return;
    setSavingPath(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/lms/learning-path/save`, {
        tier,
        steps: path.steps,
      });
      setSavedPath({ ...savedPath, tier, steps: d.steps });
      toast.success(`Saved learning path for ${tier} — members will see this order.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPath(false);
    }
  };

  const toggleEnforce = async () => {
    const next = !(savedPath?.enforce_prerequisites);
    try {
      const { data: d } = await axios.put(`${API}/admin/lms/learning-path/enforce`, {
        tier,
        enforce_prerequisites: next,
      });
      setSavedPath({ ...(savedPath || {}), tier, enforce_prerequisites: d.enforce_prerequisites });
      toast.success(
        d.enforce_prerequisites
          ? `Prerequisite lock ON — members must complete step N before step N+1 unlocks.`
          : `Prerequisite lock OFF — all courses in this tier are freely accessible.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const stats = data?.stats || { courses: 0, modules: 0, lessons: 0, quiz_questions: 0, published: 0 };

  const groupedClusters = useMemo(() => {
    const out = { thin: [], medium: [], strong: [] };
    for (const c of (data?.topic_clusters || [])) {
      (out[c.coverage] || out.medium).push(c);
    }
    return out;
  }, [data]);

  const toggleCourse = (id) => setExpandedCourses((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Card className="mb-4 border-primary/30" data-testid="curriculum-map-panel">
      {/* Header row — always visible */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
        data-testid="curriculum-map-toggle"
      >
        <div className="flex items-center gap-2 text-left">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <MapIcon className="w-4 h-4 text-primary" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Curriculum map</div>
            <div className="font-bold text-sm">
              What&apos;s covered right now, per tier
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500 hidden md:block">
          {open ? 'Hide' : 'Show'}
        </div>
      </button>

      {open ? (
        <CardContent className="pt-0 pb-4 space-y-4">
          {/* Tier selector + CTAs */}
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tier:</span>
            {TIERS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTier(t.value)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  tier === t.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`curriculum-tier-${t.value}`}
              >
                {t.label}
              </button>
            ))}
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSuggestForTier?.(tier)}
              data-testid="curriculum-suggest-tier"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />
              Suggest more for {tier}
            </Button>
            <Button
              size="sm"
              onClick={() => onBuildForTier?.(tier, null)}
              data-testid="curriculum-build-tier"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Build course for {tier}
            </Button>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-2">
            <StatTile label="Courses"   value={stats.courses} />
            <StatTile label="Published" value={stats.published} />
            <StatTile label="Modules"   value={stats.modules} />
            <StatTile label="Lessons"   value={stats.lessons} />
            <StatTile label="Quiz Qs"   value={stats.quiz_questions} />
          </div>

          {loading ? (
            <div className="text-center text-sm text-slate-500 py-8">Analyzing curriculum…</div>
          ) : (data?.stats?.courses ?? 0) === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">
              No courses yet for the <b>{tier}</b> tier.
              <div className="mt-2">
                <Button size="sm" onClick={() => onBuildForTier?.(tier, null)}>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                  Build the first {tier} course
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Weak areas — actionable */}
              {groupedClusters.thin.length > 0 ? (
                <div className="rounded-md border border-rose-200 dark:border-rose-800/60 bg-rose-50/50 dark:bg-rose-950/20 p-3" data-testid="curriculum-weak-areas">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-rose-800 dark:text-rose-200">
                      Thin coverage — likely gaps
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {groupedClusters.thin.slice(0, 20).map((c) => (
                      <button
                        key={c.keyword}
                        type="button"
                        onClick={() => onBuildForTier?.(tier, c.keyword)}
                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-slate-900 border border-rose-300 hover:border-rose-500 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors"
                        title={`Only ${c.lesson_count} lesson${c.lesson_count === 1 ? '' : 's'} touches this — click to build a course around it`}
                        data-testid={`curriculum-weak-${c.keyword}`}
                      >
                        {c.keyword} <span className="text-rose-600 font-bold">·{c.lesson_count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    Click a chip to open the AI builder seeded with that topic.
                  </div>
                </div>
              ) : null}

              {/* Strong areas — reassurance */}
              {groupedClusters.strong.length > 0 ? (
                <div className="rounded-md border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/40 dark:bg-emerald-950/20 p-3" data-testid="curriculum-strong-areas">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-emerald-800 dark:text-emerald-200">
                      Well covered
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {groupedClusters.strong.slice(0, 20).map((c) => (
                      <span
                        key={c.keyword}
                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-slate-900 border border-emerald-300"
                      >
                        {c.keyword} <span className="text-emerald-700 font-bold">·{c.lesson_count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Semantic themes — clusters lessons by meaning, not spelling */}
              {(data?.semantic_themes || []).length > 0 ? (
                <div className="rounded-md border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-950/20 p-3" data-testid="curriculum-semantic-themes">
                  <div className="flex items-center gap-2 mb-2">
                    <Network className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-indigo-800 dark:text-indigo-200">
                      Semantic themes
                    </span>
                    <span className="text-[10px] text-indigo-700/70 dark:text-indigo-300/70">
                      lessons grouped by meaning · click a thin theme to build for it
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {(data.semantic_themes).map((t, i) => {
                      const meta = COVERAGE_META[t.coverage] || COVERAGE_META.medium;
                      const isOpen = !!expandedThemes[i];
                      const courseList = t.courses || [];
                      return (
                        <div
                          key={i}
                          className="rounded-md border border-indigo-200 dark:border-indigo-800/50 bg-white dark:bg-slate-900 overflow-hidden"
                          data-testid={`curriculum-theme-${i}`}
                        >
                          <div className="flex items-start gap-2 p-2">
                            <button
                              type="button"
                              onClick={() => setExpandedThemes((p) => ({ ...p, [i]: !p[i] }))}
                              className="mt-0.5 text-slate-500 hover:text-foreground shrink-0"
                              data-testid={`theme-toggle-${i}`}
                              title={isOpen ? 'Hide courses' : 'Show related courses'}
                            >
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0 ${meta.color}`}>
                              {meta.label}
                            </span>
                            <span className="text-xs font-bold shrink-0 whitespace-nowrap">{t.lesson_count}×</span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold">{t.theme}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                e.g. {(t.example_titles || []).slice(0, 2).join(' · ')}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => onBuildForTier?.(tier, t.theme)}
                              className="text-[11px] px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 shrink-0"
                              data-testid={`theme-build-${i}`}
                            >
                              Build course
                            </button>
                          </div>
                          {isOpen ? (
                            <div className="border-t border-indigo-200/60 dark:border-indigo-800/40 bg-indigo-50/40 dark:bg-indigo-950/10 p-2 pl-8 text-[11px]">
                              {courseList.length > 0 ? (
                                <>
                                  <div className="font-bold text-slate-600 dark:text-slate-300 mb-1">
                                    Already covered in ({courseList.length}) course{courseList.length === 1 ? '' : 's'}:
                                  </div>
                                  <ul className="space-y-0.5">
                                    {courseList.map((c) => (
                                      <li key={c.id} className="text-slate-700 dark:text-slate-200">
                                        · {c.title}
                                      </li>
                                    ))}
                                  </ul>
                                  <div className="text-slate-500 italic mt-1">
                                    New course on this theme may overlap — consider adding a module to one of these instead.
                                  </div>
                                </>
                              ) : (
                                <div className="text-slate-500 italic">
                                  No existing course claims this theme — a new course would fill a real gap.
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Learning path — LLM-ordered sequence for this tier */}
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3" data-testid="curriculum-learning-path">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Route className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">
                      Recommended learning order
                    </span>
                    {savedPath?.steps?.length ? (
                      <span
                        className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                          savedPath.enforce_prerequisites
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                        }`}
                        title={savedPath.enforce_prerequisites ? 'Members must complete previous step first' : 'Saved order visible to members, no locking'}
                      >
                        {savedPath.enforce_prerequisites ? 'Locked' : 'Saved'}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1">
                    {savedPath?.steps?.length ? (
                      <Button
                        size="sm"
                        variant={savedPath.enforce_prerequisites ? 'default' : 'outline'}
                        className={savedPath.enforce_prerequisites ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                        onClick={toggleEnforce}
                        data-testid="curriculum-toggle-enforce"
                        title="When ON, members must complete the previous step before the next unlocks"
                      >
                        <Lock className="w-3.5 h-3.5 mr-1.5" />
                        {savedPath.enforce_prerequisites ? 'Locking on' : 'Locking off'}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadLearningPath}
                      disabled={pathLoading}
                      data-testid="curriculum-path-btn"
                    >
                      {pathLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Computing…</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> {path ? 'Refresh order' : 'Suggest order'}</>
                      )}
                    </Button>
                    {path?.steps?.length ? (
                      <Button
                        size="sm"
                        onClick={savePath}
                        disabled={savingPath}
                        data-testid="curriculum-save-path"
                        title="Persist this order so members see it and prerequisite locking can apply"
                      >
                        <Save className="w-3.5 h-3.5 mr-1.5" />
                        {savingPath ? 'Saving…' : 'Save order'}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {path && path.steps.length > 0 ? (
                  <ol className="space-y-1.5">
                    {path.steps.map((s) => (
                      <li key={s.course_id} className="flex items-start gap-2 text-xs" data-testid={`path-step-${s.order}`}>
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">
                          {s.order}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{s.title}</div>
                          <div className="text-[11px] text-slate-500">{s.rationale}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-[11px] text-slate-500">
                    Click <b>Suggest order</b> to have the AI arrange the current {tier} courses into a beginner → advanced path with a rationale for each step. Once you like it, <b>Save order</b> makes it visible to members and optionally locks progression.
                  </div>
                )}
              </div>

              {/* Course tree — collapsible per course */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">
                  <BookOpen className="w-3.5 h-3.5" />
                  Current {tier} courses ({data?.courses?.length || 0})
                </div>
                {(data?.courses || []).map((c) => {
                  const expanded = !!expandedCourses[c.id];
                  const totalLessons = (c.modules || []).reduce((s, m) => s + (m.lessons?.length || 0), 0);
                  return (
                    <div key={c.id} className="border border-border rounded-md" data-testid={`curriculum-course-${c.id}`}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleCourse(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleCourse(c.id);
                          }
                        }}
                        className="w-full flex items-start justify-between p-2.5 hover:bg-muted/50 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <div className="flex items-start gap-2 min-w-0">
                          {expanded ? <ChevronDown className="w-3.5 h-3.5 mt-1 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 mt-1 shrink-0" />}
                          <div className="min-w-0">
                            <div className="text-sm font-bold truncate">{c.title}</div>
                            <div className="text-[11px] text-slate-500 line-clamp-1">
                              {c.description || '—'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!c.is_published ? (
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700">Draft</span>
                          ) : null}
                          <span className="text-[10px] text-slate-500">
                            <Layers className="w-2.5 h-2.5 inline mr-0.5" />
                            {c.modules?.length || 0}m · {totalLessons}l
                          </span>
                          {onOpenCourse ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onOpenCourse(c); }}
                              className="text-[10px] font-bold px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 ml-1"
                              data-testid={`curriculum-course-open-${c.id}`}
                              title="Open this course in the editor below"
                            >
                              Edit →
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {expanded ? (
                        <div className="border-t border-border p-2.5 pl-8 space-y-2 bg-muted/30">
                          {(c.modules || []).length === 0 ? (
                            <div className="text-xs text-slate-500 italic">No modules yet.</div>
                          ) : (
                            c.modules.map((m, mi) => (
                              <div key={mi}>
                                <div className="text-xs font-semibold">M{mi + 1}. {m.title || '(untitled)'}</div>
                                {(m.lessons || []).length === 0 ? (
                                  <div className="text-[11px] text-slate-500 italic pl-4">No lessons.</div>
                                ) : (
                                  <ul className="pl-4 mt-0.5 space-y-0.5">
                                    {m.lessons.map((l, li) => (
                                      <li key={li} className="text-[11px] text-slate-600 dark:text-slate-300 flex justify-between gap-2">
                                        <span className="truncate">L{li + 1}. {l.title || '(untitled)'}</span>
                                        <span className="text-slate-400 shrink-0">
                                          {l.duration_minutes ? `${l.duration_minutes}m` : ''}
                                          {l.quiz_count ? ` · ${l.quiz_count}q` : ''}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      ) : null}
    </Card>
  );
}
